import React, { useCallback, useMemo, useRef, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import useIntls from '@opm/hooks/useIntls';
import { Segment } from 'tea-component';
import { cardIcon, tableIcon } from '@op/constants/chart';
import TableView from './TableView';
import CardListView from './CardListView';
import { useCompetitorsAdCreativesData, useExtInfo } from '../hooks';
import './index.scss';
import SortBySelect from '@op/components/SelectComponents/SortBySelect';
import { ADSortOptions } from '../constants';
import MediaPreviewModal, { useMediaPreviewModal } from './MediaPreviewModal';
import { DefaultImg } from '@op/constants/defaultImg';
import AiImgWhite from '@op/assets/svg/icon-ai-white.svg';

type TTabType = 'video' | 'ai_tag' | 'ai';

/** 各 tab 对应的表格横向滚动位置（px） */
const SCROLL_LEFT_MAP: Record<TTabType, number> = {
  video: 0,
  ai_tag: 1200,
  ai: 2200,
};

export interface ICompetitorsAdCreativesTableProps {
  filter: any;
  defaultView?: 'table' | 'card';
  verticalCard?: boolean;
}

/**
 * Competitors Ad Creatives 表格组件
 */
export default function CompetitorsAdCreativesTable({
  filter,
  defaultView = 'table',
  verticalCard = false,
}: ICompetitorsAdCreativesTableProps) {
  const { intl } = useIntls();
  const tableRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<TTabType>('video');

  const {
    sortBy,
    setSortBy,
    download,
    cardRecords,
    tableRecords,
    total,
    maxStats,
    pageInfo,
    changePageInfo,
    nextPage,
    loading,
    isFirstLoading,
    isEmpty,
    isPaginationLoading,
    keyword,
    setKeyword,
    type,
    viewMode,
    setViewMode,
    setSearchVideoTags,
    hasVideoTags,
    setHasVideoTags,
  } = useCompetitorsAdCreativesData({
    filter,
    defaultViewMode: defaultView,
  });

  // 提取 game_ids 用于 ext_info 请求
  const gameIds = useMemo(() => {
    const ids = new Set<string>();
    tableRecords.forEach((r: any) => {
      if (r.game_id) ids.add(r.game_id);
    });
    return Array.from(ids);
  }, [tableRecords]);

  // 批量获取 ext_info（AI Tags + Content Summary）
  const { extInfoMap, extInfoLoading } = useExtInfo({
    records: tableRecords,
    gameIds,
  });

  /** 切换 tab 时横向滚动表格到对应列区域 */
  const scrollTableToTab = useCallback((value: TTabType) => {
    if (!tableRef.current) return;
    const tableBody = tableRef.current.querySelector('.tea-table__body');
    if (tableBody) {
      tableBody.scrollLeft = SCROLL_LEFT_MAP[value] || 0;
    }
  }, []);

  // Segment 选项
  const segmentOptions = useMemo(
    () => [
      {
        text: intl('Creative Metrics'),
        value: 'video',
      },
      {
        text: (
          <div className="dbo-inlineflex-w16">
            <img src={tab === 'ai_tag' ? AiImgWhite : DefaultImg.AI} className="dbo-inlineflex-w16-img" />
            {intl('AI Tags')}
          </div>
        ),
        value: 'ai_tag',
      },
      {
        text: (
          <div className="dbo-inlineflex-w16">
            <img src={tab === 'ai' ? AiImgWhite : DefaultImg.AI} className="dbo-inlineflex-w16-img" />
            {intl('AI Core Content')}
          </div>
        ),
        value: 'ai',
      },
    ],
    [intl, tab],
  );

  const { visible, open, close, previewData } = useMediaPreviewModal();

  const leftFilter = useMemo(() => {
    if (viewMode === 'table') {
      return [{
        key: 'search' as const,
        element: (
          <Segment
            className="segment-light"
            value={tab}
            onChange={(value: any) => {
              setTab(value);
              scrollTableToTab(value);
            }}
            options={segmentOptions}
          />
        ),
      }];
    }
    return [];
  }, [viewMode, tab, segmentOptions]);

  return (
    <div className='dbom-competitors-ad-creatives'>
      <FilterBoxCommon
        left={leftFilter}
        right={[
          {
            key: 'singleCheckbox' as const,
            valueKey: 'showTaggedOnly',
            defaultValue: hasVideoTags,
            defaultParams: { text: 'Show Tagged Videos Only', className: "dbo-filter-box-common-checkbox" },
          },
          {
            key: 'search' as const,
            defaultValue: keyword,
            defaultParams: {
              placeholder: intl('Search Creative title or body'),
              style: { width: 260 },
              onSearch: (value: string) => setKeyword(value),
              onClear: () => setKeyword(''),
            },
          },
          {
            key: 'download' as const,
            style: { marginLeft: 10 },
            event: { onClick: () => download() },
          },
        ]}
        onChange={(values: any, key?: string) => {
          if (key === 'showTaggedOnly') {
            setHasVideoTags(!!values.showTaggedOnly);
          }
        }}
        className={`dbom-competitors-ad-creatives-filter ${viewMode !== 'table' ? 'hide-checkbox' : ''}`}
      />
      <CardCommon
        className="dbom-competitors-ad-creatives-table-card"
        operations={[cardIcon, tableIcon]}
        title={
          <>
            {viewMode !== 'table' && (
              <SortBySelect
                options={ADSortOptions}
                defaultSortBy={sortBy!}
                changeSortBy={setSortBy}
                className="dbom-sort-by-select"
              />
            )}
          </>
        }
        defaultOperation={viewMode}
        alwaysShowOperations
        handleOperationsOnClick={(item: any) => setViewMode(item.symbol)}
      >
        {viewMode === 'table' ? (
          <TableView
            tableRef={tableRef}
            data={tableRecords}
            total={total}
            maxStats={maxStats}
            pageInfo={pageInfo}
            changePageInfo={changePageInfo}
            loading={loading}
            sortBy={sortBy || { order: 'desc', by: 'impression_number' }}
            setSortBy={setSortBy}
            type={type}
            open={open}
            extInfoMap={extInfoMap}
            extInfoLoading={extInfoLoading}
            setSearchVideoTags={setSearchVideoTags}
          />
        ) : (
          <CardListView
            data={cardRecords}
            isFirstLoading={isFirstLoading}
            isEmpty={isEmpty}
            isPaginationLoading={isPaginationLoading}
            nextPage={nextPage}
            open={open}
            vertical={verticalCard}
          />
        )}
      </CardCommon>
      <MediaPreviewModal
        visible={visible}
        onClose={close}
        {...(previewData || {})}
      />
    </div>
  );
}
